import { Box, ButtonBase, Skeleton, Stack, Typography } from '@mui/material';
import { ChevronRight, Settings2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import type { GetUserQuery } from '@graphql/__generated__/graphql';
import { CardHeaderIconActions } from '@modules/_shared/components/CardHeaderIconActions';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { isAnyChannelOn } from '@modules/notifications/notificationChannels';
import { NOTIFICATION_PREFERENCES_ANCHORS } from '@modules/notifications/notificationPreferencesTour';
import {
  getGroupHeading,
  getGroupKey,
} from '@modules/notifications/preferences/notificationGroups';
import { NotificationPreferencesPanel } from '@modules/notifications/preferences/NotificationPreferencesPanel';
import { useUserNotificationSettings } from '@modules/notifications/useUserNotificationSettings';
import { useAutoTour } from '@modules/product-updates/tours/TourProvider';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import { useLocale } from '@shared/hooks/locale';

export const NOTIFICATIONS_DRAWER_PARAM = 'notifications';
const ALL_NOTIFICATION_GROUPS = 'all';

type UserNotificationsCardProps = {
  user: Pick<NonNullable<GetUserQuery['users_by_pk']>, 'id'>;
};

// One row per group (General, Leads, …) with how many of its notifications
// the user gets on at least one channel. A row opens the drawer on that group.
const UserNotificationsSummary = ({
  userId,
  onOpenGroup,
}: {
  userId: string;
  onOpenGroup: (groupKey: string) => void;
}) => {
  const { t } = useLocale();
  const { query, groups } = useUserNotificationSettings(userId);

  if (query.loading && query.data == null) {
    return (
      <Stack sx={{ gap: 1 }}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={32} />
        ))}
      </Stack>
    );
  }

  if (query.error != null) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('Notification settings could not be loaded.')}
      </Typography>
    );
  }

  if (groups.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.disabled' }}>
        {t('No notifications for this user.')}
      </Typography>
    );
  }

  return (
    <Stack sx={{ mx: -1 }}>
      {groups.map(group => (
        <ButtonBase
          key={getGroupKey(group)}
          onClick={() => onOpenGroup(getGroupKey(group))}
          sx={{
            justifyContent: 'space-between',
            gap: 1,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
            {getGroupHeading(group.module, t)}
          </Typography>
          <Stack
            direction="row"
            sx={{ alignItems: 'center', gap: 0.5, color: 'text.secondary' }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              {t('{{active}} of {{total}} active', {
                active: group.settings.filter(isAnyChannelOn).length,
                total: group.settings.length,
              })}
            </Typography>
            <ChevronRight size={16} />
          </Stack>
        </ButtonBase>
      ))}
    </Stack>
  );
};

export const UserNotificationsCard = ({ user }: UserNotificationsCardProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [searchParams, setSearchParams] = useSearchParams();
  const drawerParam = searchParams.get(NOTIFICATIONS_DRAWER_PARAM);
  const drawerGroup =
    drawerParam === ALL_NOTIFICATION_GROUPS ? null : drawerParam;
  const openDrawer = (groupKey: string | null) =>
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.set(NOTIFICATIONS_DRAWER_PARAM, groupKey ?? ALL_NOTIFICATION_GROUPS);
      return next;
    });
  const closeDrawer = () =>
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.delete(NOTIFICATIONS_DRAWER_PARAM);
        return next;
      },
      { replace: true },
    );
  const canEdit = me?.is_admin === true || me?.id === user.id;
  const { user: notificationUser, groups } = useUserNotificationSettings(
    user.id,
    { skip: !canEdit },
  );

  // "Show me" on the notification preferences entry, on your own page.
  useAutoTour('notification-preferences', canEdit && me?.id === user.id);

  if (!canEdit) {
    return null;
  }

  const name = [notificationUser?.first_name, notificationUser?.last_name]
    .filter(part => part != null && part.trim() !== '')
    .join(' ');
  const drawerTitle =
    me?.id === user.id || name === ''
      ? t('Notifications')
      : t('Notifications · {{name}}', { name });

  return (
    <>
      <ListingAccordion
        title={t('Notifications')}
        defaultExpanded
        headerAction={
          <Box
            {...tourAnchor(NOTIFICATION_PREFERENCES_ANCHORS.manage)}
            sx={{ display: 'inline-flex' }}
          >
            <CardHeaderIconActions
              actions={[
                {
                  label: t('Manage notifications'),
                  icon: <Settings2 size={16} />,
                  onClick: () => openDrawer(null),
                },
              ]}
            />
          </Box>
        }
      >
        <Box {...tourAnchor(NOTIFICATION_PREFERENCES_ANCHORS.summary)}>
          <UserNotificationsSummary userId={user.id} onOpenGroup={openDrawer} />
        </Box>
      </ListingAccordion>
      {drawerParam != null && (
        <Drawer title={drawerTitle} maxWidth={1000} onClose={closeDrawer}>
          <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
            <Box sx={{ p: 2 }}>
              <NotificationPreferencesPanel
                key={`${user.id}-${groups.length > 0}`}
                userId={user.id}
                focusGroup={drawerGroup}
              />
            </Box>
          </Box>
        </Drawer>
      )}
    </>
  );
};
